const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;
if (!AUTH_API_URL) {
    throw new Error('REACT_APP_AUTH_API_URL is required at build time');
}

export interface LoginRequest {
    username: string;
    password: string;
}

export interface AuthTokens {
    access: string;
    refresh: string;
    user: {
        uuid: string;
        username: string;
        email: string;
        is_staff: boolean;
        is_active: boolean;
        date_joined: string;
    };
}

export interface SignupRequest {
    username: string;
    email: string;
    password1: string;
    password2: string;
    terms_of_service: boolean;
    [key: string]: any; // Allow additional fields
}

export interface SignupResponse {
    status?: string;
    message?: string;
    access?: string;
    refresh?: string;
    token_type?: string;
    expires_in?: number;
    user?: {
        uuid: string;
        username: string;
        email: string;
        is_staff?: boolean;
        is_active?: boolean;
        date_joined?: string;
    };
}

export interface RefreshTokenRequest {
    refresh: string;
}

export interface RefreshTokenResponse {
    access: string;
    refresh?: string;
}

export interface LogoutResponse {
    status: string;
    message: string;
    redirect_url: string;
}

export interface PasswordResetRequest {
    username: string;
}

export interface PasswordResetResponse {
    status: string;
    message: string;
}

export interface ResetPasswordRequest {
    reset_token: string;
    new_password: string;
}

export interface ResetPasswordResponse {
    status: string;
    message: string;
}

export interface AuthError {
    error: string;
    message?: string;
}

/**
 * Login using the Lambda API Gateway endpoint
 */
export const loginWithLambda = async (credentials: LoginRequest): Promise<AuthTokens | AuthError> => {
    console.log(`[loginWithLambda] Sending login request to Auth API Gateway: ${AUTH_API_URL}`);

    try {
        const endpoint = `${AUTH_API_URL}/auth`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_type: 'login',
                username: credentials.username,
                password: credentials.password,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error(`[loginWithLambda] Auth API Gateway error:`, message);
            return { error: message };
        }

        return data as AuthTokens;

    } catch (error) {
        console.error(`[loginWithLambda] Auth API Gateway failed:`, error);
        return { error: error instanceof Error ? error.message : 'Failed to login' };
    }
};

/**
 * Signup using the Lambda API Gateway endpoint
 */
export const signupWithLambda = async (signupData: SignupRequest): Promise<SignupResponse | AuthError> => {
    console.log(`[signupWithLambda] Sending request to Auth API Gateway`);

    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_type: 'signup',
                ...signupData,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error(`[signupWithLambda] Auth API Gateway error:`, message);
            return { error: message };
        }

        return data as SignupResponse;

    } catch (error) {
        console.error(`[signupWithLambda] Auth API Gateway failed:`, error);
        return { error: error instanceof Error ? error.message : 'Failed to signup' };
    }
};

/**
 * Refresh access token using the Lambda API Gateway endpoint
 */
export const refreshTokenWithLambda = async (refreshToken: string): Promise<RefreshTokenResponse | AuthError> => {
    console.log(`[refreshTokenWithLambda] Sending request to Auth API Gateway`);

    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_type: 'refresh_token',
                refresh: refreshToken,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error(`[refreshTokenWithLambda] Auth API Gateway error:`, message);
            return { error: message };
        }

        return data as RefreshTokenResponse;

    } catch (error) {
        console.error(`[refreshTokenWithLambda] Auth API Gateway failed:`, error);
        return { error: error instanceof Error ? error.message : 'Failed to refresh token' };
    }
};

/**
 * Logout using the Lambda API Gateway endpoint
 */
export const logoutWithLambda = async (): Promise<LogoutResponse | AuthError> => {
    console.log(`[logoutWithLambda] Sending request to Auth API Gateway`);

    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_type: 'logout',
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error(`[logoutWithLambda] Auth API Gateway error:`, message);
            return { error: message };
        }

        return data as LogoutResponse;

    } catch (error) {
        console.error(`[logoutWithLambda] Auth API Gateway failed:`, error);
        return { error: error instanceof Error ? error.message : 'Failed to logout' };
    }
};

/**
 * Request password reset using the Lambda API Gateway endpoint
 */
export const requestPasswordResetWithLambda = async (username: string): Promise<PasswordResetResponse | AuthError> => {
    console.log(`[requestPasswordResetWithLambda] Sending request to Auth API Gateway`);

    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_type: 'request_password_reset',
                username: username,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error(`[requestPasswordResetWithLambda] Auth API Gateway error:`, message);
            return { error: message };
        }

        return data as PasswordResetResponse;

    } catch (error) {
        console.error(`[requestPasswordResetWithLambda] Auth API Gateway failed:`, error);
        return { error: error instanceof Error ? error.message : 'Failed to request password reset' };
    }
};

/**
 * Reset password using the Lambda API Gateway endpoint
 */
export const resetPasswordWithLambda = async (resetToken: string, newPassword: string): Promise<ResetPasswordResponse | AuthError> => {
    console.log(`[resetPasswordWithLambda] Sending request to Auth API Gateway`);

    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                request_type: 'reset_password',
                reset_token: resetToken,
                new_password: newPassword,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error(`[resetPasswordWithLambda] Auth API Gateway error:`, message);
            return { error: message };
        }

        return data as ResetPasswordResponse;

    } catch (error) {
        console.error(`[resetPasswordWithLambda] Auth API Gateway failed:`, error);
        return { error: error instanceof Error ? error.message : 'Failed to reset password' };
    }
};
